import { AuthInterceptor, BASE_URL } from "../services/AuthInterceptor.Service";

// Shared helpers for viewing/downloading order reports (admin).

const getReportExtension = (report = {}) => {
  const fromExtension = report.extension || report.fileExtension || report.reportExtension;
  if (fromExtension) {
    const cleanExtension = String(fromExtension).trim().toLowerCase();
    return cleanExtension.startsWith(".") ? cleanExtension : `.${cleanExtension}`;
  }

  const fileName = report.originalFileName || report.fileName || report.name || report.reportUrl || "";
  const cleanPath = String(fileName).split("?")[0];
  const extension = cleanPath.includes(".") ? cleanPath.slice(cleanPath.lastIndexOf(".")).toLowerCase() : "";
  return extension.includes("/") ? "" : extension;
};

const getFallbackReportFileName = (report = {}) => {
  const sourceName = report.originalFileName || report.fileName || report.name || "report";
  const cleanName = String(sourceName).replace(/\\/g, "/").split("/").pop().replace(/[\r\n"]/g, "").trim() || "report";
  const extension = getReportExtension(report);
  return cleanName.includes(".") || !extension ? cleanName : `${cleanName}${extension}`;
};

const parseFileNameFromDisposition = (contentDisposition) => {
  if (!contentDisposition) return "";
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ""));

  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1]?.trim() || "";
};

const shouldOpenReportInline = (report = {}) => {
  const extension = getReportExtension(report);
  const mimeType = String(report.mimeType || "").split(";")[0].trim().toLowerCase();
  return [".pdf", ".jpg", ".jpeg", ".png"].includes(extension) ||
    ["application/pdf", "image/jpeg", "image/jpg", "image/png"].includes(mimeType);
};

const downloadReport = async (report, notify) => {
  const response = await AuthInterceptor(`/admin/order/report/${report._id}/view`, {
    method: "GET",
    responseType: "blob",
  });

  if (response?.status !== 200) {
    notify?.({ notification: { message: "Failed to download report", type: "error" } });
    return;
  }

  const headerFileName = response.headers?.["x-report-filename"]
    ? decodeURIComponent(response.headers["x-report-filename"])
    : parseFileNameFromDisposition(response.headers?.["content-disposition"]);
  const fileName = headerFileName || getFallbackReportFileName(report);
  const reportUrl = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = reportUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(reportUrl), 60_000);
};

export const openReport = async (report, notify) => {
  const token = localStorage.getItem("token");
  if (!token) {
    notify?.({ notification: { message: "Please login again to view reports", type: "error" } });
    return;
  }

  if (!report?._id) {
    notify?.({ notification: { message: "Report details are missing", type: "error" } });
    return;
  }

  if (!shouldOpenReportInline(report)) {
    await downloadReport(report, notify);
    return;
  }

  const reportUrl = `${BASE_URL}/admin/order/report/${report._id}/view?token=${encodeURIComponent(token)}`;
  const reportWindow = window.open(reportUrl, "_blank", "noopener,noreferrer");
  if (!reportWindow) {
    notify?.({ notification: { message: "Please allow popups to view this report", type: "error" } });
  }
};


// Names of the order members who get a printed (hard copy) report.
export const getHardCopyMemberNames = (order) =>
  (Array.isArray(order?.members) ? order.members : [])
    .filter((member) => member?.hardCopy)
    .map((member) => member.fullName || "Member");
